/**
 * TanStack Query-based hooks for Medplum FHIR operations.
 *
 * This module provides React hooks that use TanStack Query for managing FHIR resources
 * with automatic caching, background refetching, and optimistic updates.
 *
 * @module medplum-tanstack
 *
 * @example
 * ```tsx
 * import { useSearchResources, medplumQueryKeys, medplumCacheUtilities } from '@repo/hooks/medplum-tanstack';
 * import { useQueryClient } from '@tanstack/react-query';
 *
 * function PatientList() {
 *   const queryClient = useQueryClient();
 *
 *   // Fetch patients with automatic caching
 *   const { data: patients = [], isLoading } = useSearchResources('Patient', {
 *     active: true
 *   });
 *
 *   // Map to view models
 *   const { data: patientViewModels = [] } = useSearchResources('Patient', { active: true }, {
 *     select: (patients) => patients.map(patient => ({
 *       id: patient.id,
 *       displayName: patient.name?.[0]?.text || 'Unknown'
 *     }))
 *   });
 *
 *   // Invalidate cache after an operation
 *   const handleUpdate = async () => {
 *     await medplumCacheUtilities.invalidateResource(queryClient, 'Patient');
 *   };
 *
 *   return <div>{patients.map(p => <div key={p.id}>{p.name?.[0]?.text}</div>)}</div>;
 * }
 * ```
 */

export { useSearch, useSearchOne, useSearchResources } from './use-search';
export type { SearchOptions } from './use-search';

export { useResource, resourceQueryKeys } from './use-resource';
export type { UseResourceOptions } from './use-resource';

export { createSearchQueryKey, medplumQueryKeys } from './query-keys';
export type { MedplumSearchQueryKey } from './query-keys';

export { medplumCacheUtilities as medplumCacheUtilities } from './cache-utils';

export { useDebouncedValue } from './use-debounced-value';
export type { UseDebouncedValueOptions } from './use-debounced-value';

export {
  useCreateResource,
  useUpdateResource,
  useUpsertResource,
  usePatchResource,
  useDeleteResource,
  useExecuteBatch,
  useCreateBinary,
  useReadResource,
} from './use-mutations';
export type { MutationOptions, CreateBinaryInput } from './use-mutations';
